import { useSyncExternalStore } from "react";

/**
 * Oppfrisk-knappen på prosjektsiden.
 *
 * API-et svarer prosjektdata (økonomi, kalkyle, fremdrift, rapport-KPI,
 * selskapssjekk) fra cache, så det går raskt å klikke inn på et prosjekt.
 * Når brukeren trykker «Oppdater tall», skal hvert API-kall for prosjektet
 * gå én gang med ?fresh=1: API-et regner da ut på nytt og oppdaterer cachen.
 *
 * I stedet for å endre hver hentefunksjon (lib/economy.ts, lib/kalkyle.ts …)
 * legger en fetch-innpakning på fresh=1 for GET /api/projects/{id}/… etter
 * startOppfrisk(id) – til det ferske svaret for adressen er mottatt. Kall som
 * starter mens det ferske er underveis, er også ferske, så et samtidig kall
 * (to komponenter, eller StrictMode i dev) aldri viser gamle tall.
 * Samme mønster som lib/apiBase.ts.
 * KI-forklaringen (report-explain) unntas: den er dyr og er ikke BizView-tall.
 */

const PROSJEKT_KALL = /^\/api\/projects\/(\d+)\/([^?#]+)/;
const UNNTAK = /^report-explain\b/;

/** projectId → adresser (sti + søk) der det ferske svaret er mottatt i denne runden */
const runder = new Map<number, Set<string>>();
let pagaende = 0;
const lyttere = new Set<() => void>();
const varsle = () => lyttere.forEach((l) => l());

/** Neste kall for hver av prosjektets adresser hentes ferskt (?fresh=1). */
export function startOppfrisk(projectId: number): void {
  runder.set(projectId, new Set());
}

function medFresh(url: string): { url: string; ferdig: () => void } | null {
  const m = PROSJEKT_KALL.exec(url);
  if (!m) return null;
  const runde = runder.get(Number(m[1]));
  if (!runde || UNNTAK.test(m[2])) return null;
  const adresse = url.replace(/([?&])fresh=[^&]*&?/, "$1").replace(/[?&]$/, "");
  if (runde.has(adresse)) return null;
  return {
    url: adresse + (adresse.includes("?") ? "&" : "?") + "fresh=1",
    ferdig: () => runde.add(adresse),
  };
}

/** Installeres i main.tsx etter installApiBase (så den ser relative «/api»-adresser). */
export function installOppfrisk(): void {
  const forrige = window.fetch.bind(window);
  window.fetch = async (input: RequestInfo | URL, init?: RequestInit) => {
    const metode = (init?.method ?? "GET").toUpperCase();
    const fersk = typeof input === "string" && metode === "GET" ? medFresh(input) : null;
    if (!fersk) return forrige(input, init);
    pagaende++;
    varsle();
    try {
      const svar = await forrige(fersk.url, init);
      if (svar.ok) fersk.ferdig(); // mislykket: neste kall prøver ferskt igjen
      return svar;
    } finally {
      pagaende--;
      varsle();
    }
  };
}

/** true mens oppfriskede kall er underveis (for knappens «Oppdaterer …»). */
export function useOppfriskPagar(): boolean {
  return useSyncExternalStore(
    (l) => {
      lyttere.add(l);
      return () => lyttere.delete(l);
    },
    () => pagaende > 0
  );
}
